import React, { useEffect, useState } from "react";
import {
  CheckCircle,
  XCircle,
  Path,
  CurrencyDollar,
  ChartLine,
  ArrowRight,
} from "@phosphor-icons/react";
import { fmtMoney, fmtRPM } from "@/lib/defaults";
import { useLandingT } from "@/lib/landingI18n";
import { LANDING } from "@/constants/landingTestIds";

/**
 * Animated live widget that cycles between a bad load and a good load
 * to visually sell the product.
 */
const LOADS = [
  {
    pick: "Danville, KY",
    delivery: "Charlotte, NC",
    loaded: 381,
    empty: 50,
    rate: 1100,
    cpm: 1.6,
    dpm: 0.65,
    feePct: 10,
    goal: 300,
  },
  {
    pick: "Dallas, TX",
    delivery: "Atlanta, GA",
    loaded: 780,
    empty: 60,
    rate: 2600,
    cpm: 1.55,
    dpm: 0.62,
    feePct: 8,
    goal: 300,
  },
];

const compute = (l) => {
  const total = l.loaded + l.empty;
  const rpm = l.rate / total;
  const trip = total * l.cpm;
  const driver = total * l.dpm;
  const fee = l.rate * (l.feePct / 100);
  const net = l.rate - (trip + driver + fee);
  const ppm = net / total;
  return { total, rpm, trip, driver, fee, net, ppm, profitable: net >= l.goal };
};

const Row = ({ label, value, tone = "default" }) => {
  const toneCls =
    tone === "positive"
      ? "text-emerald-300"
      : tone === "negative"
        ? "text-red-300"
        : "text-slate-100";
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-b-0">
      <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-medium">
        {label}
      </span>
      <span className={`tabular text-sm font-medium ${toneCls}`}>{value}</span>
    </div>
  );
};

export const HeroLiveWidget = ({ lang }) => {
  const t = useLandingT(lang);
  const [idx, setIdx] = useState(0);
  const load = LOADS[idx];
  const m = compute(load);

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % LOADS.length), 3400);
    return () => clearInterval(id);
  }, []);

  const verdict = m.profitable
    ? {
        bg: "bg-emerald-500/12",
        border: "neon-pulse border-emerald-500/40",
        icon: <CheckCircle size={22} weight="fill" className="text-emerald-400" />,
        label: t("widgetProfitable"),
        text: "text-emerald-300",
      }
    : {
        bg: "bg-red-500/12",
        border: "crimson-pulse border-red-500/40",
        icon: <XCircle size={22} weight="fill" className="text-red-400" />,
        label: t("widgetUnprofitable"),
        text: "text-red-300",
      };

  return (
    <div
      data-testid={LANDING.liveWidget}
      className="relative w-full max-w-[540px] mx-auto"
    >
      {/* Glow backdrop */}
      <div
        aria-hidden
        className="absolute -inset-6 rounded-[36px] opacity-70 blur-3xl"
        style={{
          background: m.profitable
            ? "radial-gradient(closest-side, rgba(16,185,129,0.35), transparent 70%)"
            : "radial-gradient(closest-side, rgba(239,68,68,0.32), transparent 70%)",
          transition: "background 500ms ease",
        }}
      />

      {/* Card */}
      <div className="relative glass-strong rounded-3xl p-5 md:p-6">
        <div className="scanline rounded-3xl" />

        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-medium">
              Live Screening
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {LOADS.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === idx ? "w-6 bg-emerald-400" : "w-2 bg-slate-600"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Route */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 grid place-items-center">
            <Path size={16} weight="duotone" className="text-emerald-400" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-slate-400">
              <span>{t("widgetPickCity")}</span>
              <span className="text-slate-600">→</span>
              <span>{t("widgetDelivery")}</span>
            </div>
            <div className="text-white text-base font-semibold leading-tight">
              {load.pick} → {load.delivery}
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
              {t("widgetRate")}
            </div>
            <div className="text-white tabular text-lg font-semibold mt-1">
              {fmtMoney(load.rate)}
            </div>
          </div>
          <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
              {t("widgetMiles")}
            </div>
            <div className="text-white tabular text-lg font-semibold mt-1">
              {m.total.toLocaleString()}
            </div>
          </div>
          <div className="rounded-xl border border-white/8 bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400">
              {t("widgetRpm")}
            </div>
            <div className="text-white tabular text-lg font-semibold mt-1">
              {fmtRPM(m.rpm)}
            </div>
          </div>
        </div>

        {/* Detail rows */}
        <div className="mt-4 rounded-xl border border-white/8 bg-white/[0.02] px-3 py-1">
          <Row label="Trip Cost" value={fmtMoney(m.trip)} tone="negative" />
          <Row label="Driver Pay" value={fmtMoney(m.driver)} tone="negative" />
          <Row label="Dispatcher Fee" value={fmtMoney(m.fee)} tone="negative" />
          <Row
            label={t("widgetNet")}
            value={fmtMoney(m.net)}
            tone={m.profitable ? "positive" : "negative"}
          />
        </div>

        {/* Verdict */}
        <div
          className={`mt-4 rounded-2xl border ${verdict.bg} ${verdict.border} p-4 flex items-center gap-3 transition-colors duration-500`}
        >
          <div className="w-10 h-10 rounded-xl grid place-items-center bg-white/5 border border-white/10">
            {verdict.icon}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-medium">
              Verdict
            </div>
            <div className={`text-lg font-semibold tracking-tight ${verdict.text}`}>
              {verdict.label}
            </div>
          </div>
          <div className="text-end">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400">
              {t("widgetGoal")}
            </div>
            <div className="tabular text-sm font-semibold text-slate-100">
              {fmtMoney(load.goal)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
